// askSmart-ui/src/pages/MapleCoin/operations/LedgerTab.tsx
//
// The institutional ledger, read-only: journals in sequence with their exact
// debit/credit entries, reversal links and seal position, plus the integrity
// verification. Journals are immutable — there is no create, edit or delete
// here, and filters are fixed fields, never free-form queries. Seal hashes
// appear only inside a journal's technical integrity detail.
import { FormEvent, ReactNode, useCallback, useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { IntegrityStatus, Journal, JournalDetail, JournalFilter, OpsApiError, getIntegrity, getJournal, listJournals } from './opsApi';
import { Busy, Entries, Identity, KeyValues, Loading, Panel, Unavailable, dateTime, dayStart, titleCase } from './shared';
import { formatMoney } from '../money';
import IntegrityPanel from './IntegrityPanel';
import { OpsNav } from './MapleCoinOperations';

const PAGE = 25;

function JournalView({ seq, onBack, nav, onOpenJournal }: { seq: string; onBack: () => void; nav: OpsNav; onOpenJournal: (s: string) => void }) {
  const [j, setJ] = useState<JournalDetail | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error' | 'missing'>('loading');
  const load = useCallback(async () => {
    try {
      setJ(await getJournal(seq));
      setState('ready');
    } catch (err) {
      setState(err instanceof OpsApiError && err.status === 404 ? 'missing' : 'error');
    }
  }, [seq]);
  useEffect(() => { setState('loading'); load(); }, [load]);

  return (
    <div className="mco-stack">
      <button type="button" className="mco-back" onClick={onBack}><ArrowLeft size={14} /> Ledger</button>
      {state === 'loading' && <Loading />}
      {state === 'error' && <Unavailable onRetry={load} />}
      {state === 'missing' && <Panel><p>Journal #{seq} was not found.</p></Panel>}
      {state === 'ready' && j && (
        <>
          <Panel title={`Journal #${j.journalSeq}`}>
            <KeyValues rows={[
              ['Journal ID', <code key="Journal ID">{j.journalId}</code>],
              ['Type', titleCase(j.journalType)],
              ['Effective date', j.effectiveDate],
              ['Posted', dateTime(j.postedAt)],
              ['Actor', <span key="Actor">{titleCase(j.actorType)} · <Identity sub={j.actorId} /></span>],
              ['Transaction', j.transactionReference
                ? <button type="button" className="mco-link" onClick={() => nav.openTransaction(j.transactionReference!)}>{j.transactionReference}</button>
                : '—'],
              ['Reason', j.reason],
              ...(j.reversesJournalSeq ? [['Reverses', <button key="Reverses" type="button" className="mco-link" onClick={() => onOpenJournal(j.reversesJournalSeq!)}>Journal #{j.reversesJournalSeq}</button>] as [string, ReactNode]] : []),
              ...(j.reversedByJournalSeq ? [['Reversed by', <button key="Reversed by" type="button" className="mco-link" onClick={() => onOpenJournal(j.reversedByJournalSeq!)}>Journal #{j.reversedByJournalSeq}</button>] as [string, ReactNode]] : []),
              ['Seal', j.sealSeq ? `#${j.sealSeq} · ${dateTime(j.sealedAt)}` : 'Not sealed'],
            ]} />
            <Entries entries={j.entries} />
          </Panel>
          <Panel title="Financial audit">
            <ul className="mco-plain">
              {j.auditEvents.map((a, i) => (
                <li key={i}>{a.action} · {titleCase(a.actorType)} <Identity sub={a.actorId} /> · {dateTime(a.occurredAt)}
                  {a.requestId && <span className="mco-dim"> · request <code>{a.requestId}</code></span>}</li>
              ))}
            </ul>
          </Panel>
          {j.seal && (
            <details className="mco-technical">
              <summary>Technical integrity detail</summary>
              <p className="mco-note">The seal links this journal's content to the previous seal. Recomputing it is what verification checks.</p>
              <KeyValues rows={[
                ['Seal sequence', `#${j.seal.sealSeq}`],
                ['Content hash', <code key="Content hash" className="mco-hash">{j.seal.contentHash}</code>],
                ['Previous seal hash', <code key="Previous seal hash" className="mco-hash">{j.seal.prevSealHash}</code>],
                ['Seal hash', <code key="Seal hash" className="mco-hash">{j.seal.sealHash}</code>],
              ]} />
            </details>
          )}
        </>
      )}
    </div>
  );
}

type Form = { from: string; to: string; journalSeq: string; journalType: string; transactionReference: string; accountCode: string };
const EMPTY: Form = { from: '', to: '', journalSeq: '', journalType: '', transactionReference: '', accountCode: '' };

const journalTotal = (j: Journal) => {
  const debit = j.entries.filter((e) => e.side === 'DEBIT').reduce((a, e) => a + BigInt(e.amountMinor), BigInt(0));
  return formatMoney(debit.toString(), j.entries[0]?.currency ?? 'CAD');
};

export default function LedgerTab({ nav, initialJournal }: { nav: OpsNav; initialJournal?: string }) {
  const [openSeq, setOpenSeq] = useState<string | null>(initialJournal ?? null);
  const [form, setForm] = useState<Form>(EMPTY);
  const [applied, setApplied] = useState<JournalFilter>({});
  const [rows, setRows] = useState<Journal[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error' | 'invalid'>('loading');
  const [more, setMore] = useState(false);
  const [integrity, setIntegrity] = useState<IntegrityStatus | null>(null);

  const load = useCallback(async (f: JournalFilter, before?: string) => {
    if (before) setMore(true); else setState('loading');
    try {
      const r = await listJournals({ ...f, limit: String(PAGE), before });
      setRows((prev) => (before ? [...prev, ...r.journals] : r.journals));
      setTypes(r.journalTypes);
      setNext(r.nextBefore);
      setState('ready');
    } catch (err) {
      setState(err instanceof OpsApiError && err.status === 400 ? 'invalid' : 'error');
    } finally {
      setMore(false);
    }
  }, []);
  useEffect(() => {
    if (openSeq) return;
    load(applied);
    getIntegrity().then(setIntegrity).catch(() => setIntegrity(null));
  }, [openSeq, load]); // eslint-disable-line react-hooks/exhaustive-deps

  if (openSeq) return <JournalView seq={openSeq} onBack={() => setOpenSeq(null)} nav={nav} onOpenJournal={setOpenSeq} />;

  const apply = (e?: FormEvent) => {
    e?.preventDefault();
    const f: JournalFilter = {
      from: dayStart(form.from), to: dayStart(form.to, true), journalSeq: form.journalSeq.trim().replace(/^#/, '') || undefined,
      journalType: form.journalType || undefined, transactionReference: form.transactionReference.trim() || undefined,
      accountCode: form.accountCode.trim() || undefined,
    };
    setApplied(f);
    load(f);
  };
  const set = (k: keyof Form) => (e: { target: { value: string } }) => setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <div className="mco-stack">
      {integrity && <IntegrityPanel status={integrity}
        onChecked={(c) => setIntegrity((s) => s && { ...s, latestCheck: c, sealsSinceCheck: '0' })} />}

      <Panel title="Journals" label="Journal filters">
        <p className="mco-note">Journals are immutable. A correction is a new reversing journal, linked to the one it reverses.</p>
        <form className="mco-filters" onSubmit={apply} aria-label="Filter journals">
          <label>From<input type="date" value={form.from} onChange={set('from')} /></label>
          <label>To<input type="date" value={form.to} onChange={set('to')} /></label>
          <label>Journal #<input value={form.journalSeq} onChange={set('journalSeq')} inputMode="numeric" /></label>
          <label>Type
            <select value={form.journalType} onChange={set('journalType')}>
              <option value="">Any type</option>
              {types.map((t) => <option key={t} value={t}>{titleCase(t)}</option>)}
            </select>
          </label>
          <label>Transaction<input value={form.transactionReference} onChange={set('transactionReference')} placeholder="T…" /></label>
          <label>Account code<input value={form.accountCode} onChange={set('accountCode')} placeholder="e.g. 236010-CAD" /></label>
          <div className="mco-filter-actions">
            <button type="submit" className="mco-btn">Apply</button>
            <button type="button" className="mco-btn mco-btn--quiet" onClick={() => { setForm(EMPTY); setApplied({}); load({}); }}>Clear</button>
          </div>
        </form>
      </Panel>

      <Panel label="Journal list">
        {state === 'loading' && <Loading />}
        {state === 'error' && <Unavailable onRetry={() => load(applied)} />}
        {state === 'invalid' && <p className="mco-error" role="alert">One of the filters is not valid.</p>}
        {state === 'ready' && (
          <>
            {rows.length === 0 && <p className="mco-empty">No journals match.</p>}
            {rows.map((j) => (
              <div key={j.journalSeq} className="mco-journal">
                <p className="mco-journal-head">
                  <button type="button" className="mco-link" onClick={() => setOpenSeq(j.journalSeq)}>Journal #{j.journalSeq}</button>
                  <span> · {titleCase(j.journalType)} · {dateTime(j.postedAt)} · {journalTotal(j)}</span>
                  {j.transactionReference && <> · <button type="button" className="mco-link" onClick={() => nav.openTransaction(j.transactionReference!)}>{j.transactionReference}</button></>}
                  {j.reversesJournalSeq && <span className="mco-tag">reverses #{j.reversesJournalSeq}</span>}
                  {j.reversedByJournalSeq && <span className="mco-tag">reversed by #{j.reversedByJournalSeq}</span>}
                  <span className="mco-dim"> · seal #{j.sealSeq ?? '—'}</span>
                </p>
                <Entries entries={j.entries} />
              </div>
            ))}
            {next && (
              <button type="button" className="mco-btn" disabled={more} onClick={() => load(applied, next)}>
                <Busy busy={more}>Load more</Busy>
              </button>
            )}
          </>
        )}
      </Panel>
    </div>
  );
}
